import type { RedirectRequest } from "@azure/msal-browser";
import { storeToRefs } from "pinia";
import { useAuthStore } from "@/store/authStore";
import { useIcs4Store } from "@/store/storeIcs4";
import { useNotificationsStore } from "@/store/notificationsStore";
import { RESTORE_TIMEOUT_MS } from "./silentRestore";
import { setUserAccount, userAzureId, userOid } from "./sessionState";
import type { MsalFlowDeps } from "@ics/auth-api";

const debugPrefix = '[Auth]';

// NB: bevisst adskilt fra `intendedRoute.ts` (sessionStorage-variant med annen
// filtrerings-semantikk for router-guarden). Denne localStorage-varianten er
// legacy-MSAL-flytens egen rundtur (lagre før redirect-login, hente etter).
const INTENDED_ROUTE_KEY = 'ics_intended_route';

/**
 * Sikkert localStorage-kall som ikke kaster feil (for private browsing mode etc.)
 */
function safeLocalStorageSet (key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignorer feil - localStorage er ikke tilgjengelig (private browsing, kvote overskredet, etc.)
    console.warn(`${debugPrefix} Kunne ikke skrive til localStorage`);
  }
}

function safeLocalStorageGet (key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageRemove (key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignorer feil
  }
}

/**
 * Lagrer intended route før redirect-login (for å komme tilbake etter login)
 */
export function saveIntendedRoute (route: string): void {
  safeLocalStorageSet(INTENDED_ROUTE_KEY, route);
  console.log(`${debugPrefix} Lagret intended route:`, route);
}

/**
 * Henter og sletter intended route etter redirect-login
 */
export function getAndClearIntendedRoute (): string | null {
  const route = safeLocalStorageGet(INTENDED_ROUTE_KEY);
  if (route) {
    safeLocalStorageRemove(INTENDED_ROUTE_KEY);
    console.log(`${debugPrefix} Hentet intended route:`, route);
  }
  return route;
}

// *************************************************
// LOGIN REDIRECT (fallback når popup er blokkert)
// *************************************************

/**
 * Logger inn med redirect-flow (fallback når popup er blokkert).
 * Lagrer intended route før redirect for å komme tilbake etter login.
 */
export async function loginRedirect (deps: MsalFlowDeps, intendedRoute?: string): Promise<void> {
  const notificationsStore = useNotificationsStore();

  try {
    const instance = await deps.getInstance();
    const tokenConfig = await deps.getTokenRequest();

    // Lagre intended route hvis spesifisert
    if (intendedRoute) {
      saveIntendedRoute(intendedRoute);
    } else {
      // Lagre current route som fallback
      saveIntendedRoute(globalThis.location.pathname + globalThis.location.search);
    }

    console.log(`${debugPrefix} Starter redirect-login...`);

    const redirectRequest: RedirectRequest = {
      ...tokenConfig,
      prompt: "select_account",
      redirectStartPage: globalThis.location.origin
    };

    await instance.loginRedirect(redirectRequest);
  } catch (error: any) {
    console.error(`${debugPrefix} Redirect login feilet:`, error);
    notificationsStore.addError(
      "Innloggingsfeil",
      "Det oppstod en feil under innlogging. Prøv igjen."
    );
  }
}

/**
 * Håndterer redirect-respons etter login.
 * Kalles ved app-start for å håndtere returnerende bruker fra Microsoft login.
 * @returns intended route string hvis bruker kom tilbake fra redirect-login, null ellers
 */
export async function handleRedirectResponse (deps: MsalFlowDeps): Promise<string | null> {
  // Cold-boot short-circuit (WI 1218): hvis URL ikke har MSAL-auth-respons-
  // indikatorer (code/state/id_token/error i hash eller query), har vi
  // garantert ingenting å håndtere. Tidligere kalte vi `handleRedirectPromise`
  // ubetinget — i MSAL v5 venter den på interaction_in_progress / BroadcastChannel-
  // state ved cold boot, og henger taust. Resultatet var at `app.mount('#app')`
  // aldri ble kalt → blank skjerm på dyplenker.
  const hash = window.location.hash;
  const search = window.location.search;
  const hasAuthResponse = /[&#?](code|state|id_token|access_token|error|error_description)=/.test(`${hash}${search}`);
  if (!hasAuthResponse) {
    return null;
  }

  try {
    const instance = await deps.getInstance();
    const responsePromise = instance.handleRedirectPromise({ hash });
    const timeoutPromise = new Promise<null>((resolve) =>
      setTimeout(() => resolve(null), RESTORE_TIMEOUT_MS)
    );
    const response = await Promise.race([responsePromise, timeoutPromise]);

    if (response?.account) {
      console.log(`${debugPrefix} Redirect-login vellykket`);
      instance.setActiveAccount(response.account);
      userAzureId.value = response.account.username;
      userOid.value = deps.resolveOid(response.account);
      setUserAccount(response.account);

      // Oppdater authStore (ny)
      const authStore = useAuthStore();
      authStore.setAuthStatus(true, response.account.name ?? null);

      // Oppdater storeIcs4 for bakoverkompatibilitet
      const ics4Store = useIcs4Store();
      const { isAuth, userName } = storeToRefs(ics4Store);
      userName.value = response.account.name || '';
      isAuth.value = true;

      // Hent intended route (uten å slette - caller navigerer)
      const intendedRoute = getAndClearIntendedRoute();
      if (intendedRoute && intendedRoute !== '/' && intendedRoute !== '/login') {
        console.log(`${debugPrefix} Intended route etter redirect-login:`, intendedRoute);
        return intendedRoute;
      }

      // Ingen spesifikk intended route, men login var vellykket
      return '/';
    }

    return null;
  } catch (error) {
    console.error(`${debugPrefix} Feil ved håndtering av redirect-respons:`, error);
    return null;
  }
}
